import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Copy, Loader2, Save, Sparkles, RotateCcw, ChevronUp, ChevronDown, Maximize2, Minimize2, ShieldX } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import type { Conflict } from '@/lib/conflictEngine';
import type { InferredLayer } from '@/lib/autoStructure';
import type { VFXLayer, VFXIntensity } from '@/lib/vfxLayerEngine';
import type { StyleTendency } from '@/lib/styleMemory';
import type { PromptFormat } from '@/lib/engineTypes';
import InlinePromptEditor from '@/components/InlinePromptEditor';
import type { PromptIssue } from '@/lib/promptInlineAnalyzer';
import { analyzeInlinePromptIssues, resolveAllPromptIssues } from '@/lib/promptInlineAnalyzer';
import { analyzePromptWithGrok, enhanceCinematicGrammarWithGrok, resolvePhysicsWithGrok, resolveAllWithGrok, resolvePromptConflictsWithGrok, quickLocalFix, type AdvancedPromptReport, type PromptIntelligenceResult, type PromptSettingsPreset } from '@/ai/grokPromptService';
import { intelligence } from '@/lib/intelligence';
import { normalizeSubject, normalizeSubjectLabel } from '@/lib/subjectUtils';
import { generateNegativePrompt, analyzeScene } from '@/lib/negativePromptEngine';
import { formatNegativePrompt, type ExtendedPromptFormat } from '@/lib/outputStyleFormatter';
import { detectConflicts, fixPrompt, enhancePrompt } from '@/lib/openrouterPromptAI';
import { getSmartDefaults } from '@/lib/smartContextEngine';
import { buildSupabaseFunctionUrl } from '@/lib/url';
import './inline-prompt-editor.css';

const EDGE_FN_URL = buildSupabaseFunctionUrl(import.meta.env.VITE_SUPABASE_URL, 'prompt-ai');

let _previewRateLimitedUntil = 0;

const callPromptAI = async (mode: string, prompt: string, extra?: Record<string, unknown>): Promise<any> => {
  if (Date.now() < _previewRateLimitedUntil) {
    console.warn('[PromptPreview] Rate limited, skipping');
    return null;
  }
  try {
    const response = await fetch(EDGE_FN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
      },
      body: JSON.stringify({ prompt, mode, ...extra }),
    });
    if (response.status === 429) {
      console.warn('AI rate limited, backing off 30s');
      _previewRateLimitedUntil = Date.now() + 30_000;
      return null;
    }
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};

interface Props {
  prompt: string;
  canvasSubject?: string | null;
  onSave: () => void;
  conflicts?: Conflict[];
  inferred?: InferredLayer[];
  locked?: boolean;
  vfxLayers?: VFXLayer[];
  vfxIntensity?: VFXIntensity;
  styleTendencies?: StyleTendency[];
  qualityScore?: number;
  onEnhance?: () => void;
  onPromptChange?: (value: string) => void;
  onDetectedSubject?: (subject: string | null) => void;
  initialPrompt?: string;
  onApplySettingsPreset?: (preset: PromptSettingsPreset) => void;
  /** State-based single issue fix (fixes scene state, not text) */
  onFixSingleIssue?: (issue: PromptIssue) => void;
  /** State-based fix-all (fixes scene state, not text) */
  onFixAllIssues?: () => void;
  /** Prompt output format */
  promptFormat?: PromptFormat;
  onFormatChange?: (f: PromptFormat) => void;
  // testing hook: gives a handle to the internal commit function
  onTestCommitFixed?: (fn: (nextPrompt: string) => Promise<void>) => void;
}

type SentenceState = 'good' | 'weak' | 'conflict';
const getHealthTone = (value: number): string => {
  if (value < 40) return 'text-red-400 border-red-500/40';
  if (value < 70) return 'text-orange-300 border-orange-500/40';
  return 'text-emerald-300 border-emerald-500/40';
};

const getHealthBg = (value: number): string => {
  if (value < 40) return 'bg-red-500';
  if (value < 70) return 'bg-orange-400';
  return 'bg-emerald-400';
};

const isPlaceholderPrompt = (value: string): boolean => {
  const text = value.trim().toLowerCase();
  if (!text) return true;
  return text.startsWith('place elements')
    || text.startsWith('add scene elements')
    || text === 'prompt preview'
    || text === 'type your prompt here';
};

const countOccurrences = (text: string, needle: string): number => {
  const source = text.toLowerCase();
  const token = needle.trim().toLowerCase();
  if (!token) return 0;

  let count = 0;
  let cursor = 0;
  while (cursor < source.length) {
    const idx = source.indexOf(token, cursor);
    if (idx === -1) break;
    count += 1;
    cursor = idx + token.length;
  }
  return count;
};

const normalizeFixedPromptText = (value: string): string => value
  .replace(/\s+,/g, ',')
  .replace(/,\s*,/g, ', ')
  .replace(/\s{2,}/g, ' ')
  .replace(/\s+\./g, '.')
  .trim();

// run the built-in inline analyzer to strip any duplicates, weak phrases,
// empty parentheses and other low-level issues.  We normalize before/after
// to keep formatting tidy.
const cleanFinalPrompt = (text: string): string => {
  let result = normalizeFixedPromptText(text);
  result = resolveAllPromptIssues(result);
  // remove stray empty parentheses
  result = result.replace(/\(\s*\)/g, '');
  return normalizeFixedPromptText(result);
};

// When the AI returns a version of the prompt that simply appends
// modifications to the original text, we end up with duplicated content
// (e.g. "foo foo fixed" when the source was "foo").  The model is
// instructed to rewrite from scratch, but occasionally it still just adds
// text on top.  To guard against that we normalize the result and strip
// extra copies of the source prompt while keeping a single instance.  This
// helps both the Fix and Enhance buttons produce clean output instead of
// growing the prompt infinitely.
const dedupeAgainstSource = (candidate: string, source: string): string => {
  let result = normalizeFixedPromptText(candidate || '');
  const src = normalizeFixedPromptText(source || '');
  if (!src) return result;

  // repeatedly remove the source prompt until only one occurrence remains
  while (countOccurrences(result, src) > 1) {
    result = result.replace(src, '').trim();
    result = normalizeFixedPromptText(result);
  }
  // if removal emptied the string, fall back to candidate so we don't
  // accidentally return an empty prompt
  return result || candidate;
};

// Quick check whether every word from the source appears in the candidate
// in the same sequence.  The fix step is not allowed to reorder words; if
// the LLM output violates this we ignore it and use the earlier version
// produced by grok.
const preservesWordOrder = (candidate: string, source: string): boolean => {
  const sw = source.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const cw = candidate.trim().toLowerCase().split(/\s+/).filter(Boolean);
  let idx = 0;
  for (const w of sw) {
    idx = cw.indexOf(w, idx);
    if (idx === -1) return false;
    idx += 1;
  }
  return true;
};

const issueStillPresentAfterFix = (before: string, after: string, issueText: string, issueType: PromptIssue['type']): boolean => {
  const needle = issueText.trim();
  if (!needle) return false;

  if (issueType === 'duplicate') {
    const beforeCount = countOccurrences(before, needle);
    const afterCount = countOccurrences(after, needle);
    return afterCount >= beforeCount;
  }

  return after.toLowerCase().includes(needle.toLowerCase());
};

const applyDeterministicSingleFix = (sourcePrompt: string, issue: PromptIssue): string => {
  const start = Math.max(0, Math.min(sourcePrompt.length, issue.start));
  const end = Math.max(start, Math.min(sourcePrompt.length, issue.end));
  if (end <= start) return sourcePrompt;

  const replacement = issue.fix?.replacement ?? '';
  const next = `${sourcePrompt.slice(0, start)}${replacement}${sourcePrompt.slice(end)}`;
  return normalizeFixedPromptText(next || sourcePrompt);
};

const hardenFixedPrompt = (candidate: string, fallback: string): string => {
  const base = normalizeFixedPromptText(candidate || fallback);
  if (!base) return normalizeFixedPromptText(fallback);

  const strict = normalizeFixedPromptText(resolveAllPromptIssues(base));
  if (!strict) return base;

  const baseIssueCount = analyzeInlinePromptIssues(base).filter((issue) => issue.type !== 'valid').length;
  const strictIssueCount = analyzeInlinePromptIssues(strict).filter((issue) => issue.type !== 'valid').length;

  if (strictIssueCount <= baseIssueCount && strict.length >= Math.max(8, base.length * 0.45)) {
    return strict;
  }

  return base;
};

const activeIssues = (items: PromptIssue[]): PromptIssue[] => items.filter((issue) => issue.type !== 'valid');

type PanelSize = 'collapsed' | 'compact' | 'expanded';

export default function PromptPreview({ prompt: incomingPrompt, canvasSubject: incomingCanvasSubject = null, onSave, locked = false, onEnhance, onPromptChange, onDetectedSubject, initialPrompt = '', onApplySettingsPreset, onFixSingleIssue, onFixAllIssues, promptFormat = 'structured', onFormatChange, onTestCommitFixed }: Props) {
  const { t } = useLanguage();
  const [prompt, setPrompt] = useState(incomingPrompt);
  const [expertMode, setExpertMode] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isResolvingAll, setIsResolvingAll] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isFixingPhysics, setIsFixingPhysics] = useState(false);
  const [isEnhancingGrammar, setIsEnhancingGrammar] = useState(false);
  const [lastPipelineStatus, setLastPipelineStatus] = useState<'idle' | 'analyzing' | 'fixing' | 'enhancing' | 'done'>('idle');
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [ignoredIssueIds, setIgnoredIssueIds] = useState<Set<string>>(new Set());
  const [issues, setIssues] = useState<PromptIssue[]>([]);
  const [promptHealth, setPromptHealth] = useState(100);
  const [advancedReport, setAdvancedReport] = useState<AdvancedPromptReport | null>(null);
  const [promptIntel, setPromptIntel] = useState<PromptIntelligenceResult | null>(null);
  const [settingsHistory, setSettingsHistory] = useState<PromptSettingsPreset[]>([]);
  const [copied, setCopied] = useState(false);
  const [zeroConflictMode, setZeroConflictMode] = useState(true);
  const [panelSize, setPanelSize] = useState<PanelSize>('compact');
  const [showInspector, setShowInspector] = useState(false);
  const [negativePrompt, setNegativePrompt] = useState('');
  const [showNegativePrompt, setShowNegativePrompt] = useState(true);
  const [copiedNeg, setCopiedNeg] = useState(false);
  const [aiConflictWarning, setAiConflictWarning] = useState('');
  const canSyncPromptRef = useRef(false);
  const hasUserEditedRef = useRef(false);
  const autoProcessingLockRef = useRef(false);
  const lastProcessedPromptRef = useRef<string | null>(null);
  const canvasSubject = incomingCanvasSubject ?? null;
  const extractedPromptSubject = promptIntel?.understoodScene.subject ?? null;
  const safeCanvasSubject = normalizeSubject(canvasSubject, extractedPromptSubject);
  const subject = normalizeSubjectLabel(canvasSubject, extractedPromptSubject);

  useEffect(() => {
    const incomingTrim = incomingPrompt.trim();
    const localTrim = latestPromptRef.current.trim();

    // Don't override local edited/fixed prompt with older parent prompt.
    // Allow sync again when parent catches up to the same value (or when parent clears prompt).
    if (hasUserEditedRef.current && incomingTrim && incomingTrim !== localTrim) {
      return;
    }

    canSyncPromptRef.current = true;
    hasUserEditedRef.current = false;

    if (incomingPrompt !== latestPromptRef.current) {
      setPrompt(incomingPrompt);
    }
    latestPromptRef.current = incomingPrompt;
  }, [incomingPrompt]);

  const lastAnalyzedRef = useRef<string>('');
  const rateLimitedUntilRef = useRef<number>(0);
  const analyzeRequestIdRef = useRef<number>(0);
  const intelRequestIdRef = useRef<number>(0);
  const latestPromptRef = useRef<string>(incomingPrompt);

  useEffect(() => {
    latestPromptRef.current = prompt;
  }, [prompt]);

  useEffect(() => {
    const timer = window.setTimeout(async () => {
      const promptSnapshot = prompt.trim();
      const requestId = ++analyzeRequestIdRef.current;

      if (isPlaceholderPrompt(promptSnapshot)) return;
      if (lastAnalyzedRef.current === promptSnapshot) return;
      if (Date.now() < rateLimitedUntilRef.current) return;

      setLastPipelineStatus('analyzing');
      setIsAnalyzing(true);
      lastAnalyzedRef.current = promptSnapshot;

      try {
        const result = await analyzePromptWithGrok(promptSnapshot, safeCanvasSubject);

        // Ignore stale analysis responses (old prompt / old request)
        if (requestId !== analyzeRequestIdRef.current) return;
        if (latestPromptRef.current.trim() !== promptSnapshot) return;

        setPromptHealth(result.health);
        setAdvancedReport(result.advancedReport ?? null);
        setIssues(result.issues.filter((issue) => !ignoredIssueIds.has(issue.id)));
        setLastPipelineStatus('done');
      } finally {
        if (requestId === analyzeRequestIdRef.current) {
          setIsAnalyzing(false);
        }
      }
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [prompt, ignoredIssueIds, safeCanvasSubject]);

  useEffect(() => {
    if (!safeCanvasSubject || subject === 'unknown') {
      console.warn(`Canvas subject not detected. Falling back to: ${subject}`);
    }
  }, [safeCanvasSubject, subject]);

  useEffect(() => {
    const timer = window.setTimeout(async () => {
      const promptSnapshot = prompt.trim();
      const requestId = ++intelRequestIdRef.current;

      if (!promptSnapshot) {
        setPromptIntel(null);
        return;
      }

      const report = await intelligence.analyzePrompt(promptSnapshot);

      // Ignore stale intelligence responses
      if (requestId !== intelRequestIdRef.current) return;
      if (latestPromptRef.current.trim() !== promptSnapshot) return;

      setPromptIntel(report);
    }, 2000);

    return () => window.clearTimeout(timer);
  }, [prompt]);

  useEffect(() => {
    onDetectedSubject?.(normalizeSubject(promptIntel?.understoodScene.subject ?? null, null));
  }, [onDetectedSubject, promptIntel?.understoodScene.subject]);

  useEffect(() => {
    if (!promptIntel) return;
    setSettingsHistory((prev) => {
      const key = JSON.stringify(promptIntel.autoDirector);
      if (prev.some((item) => JSON.stringify(item) === key)) return prev;
      return [promptIntel.autoDirector, ...prev].slice(0, 8);
    });
  }, [promptIntel]);

  useEffect(() => {
    if (!canSyncPromptRef.current) return;
    const timer = window.setTimeout(() => {
      onPromptChange?.(prompt);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [onPromptChange, prompt]);

  // ── Dynamic Negative Prompt Generation ──
  useEffect(() => {
    const trimmed = prompt.trim();
    if (!trimmed || isPlaceholderPrompt(trimmed)) {
      setNegativePrompt('');
      return;
    }
    const neg = generateNegativePrompt(trimmed);
    setNegativePrompt(neg);
  }, [prompt]);

  useEffect(() => {
    const trimmed = prompt.trim();
    if (!trimmed || isPlaceholderPrompt(trimmed)) {
      setAiConflictWarning('');
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await detectConflicts(trimmed);
        if (!cancelled) {
          setAiConflictWarning(result.hasConflicts ? result.summary : '');
        }
      } catch {
        if (!cancelled) setAiConflictWarning('');
      }
    }, 600);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [prompt]);

  // Auto-pipeline disabled to prevent excessive API calls and rate limiting.
  // Users can manually click "Fix" or "Enhance" buttons instead.

  const issueCountLabel = useMemo(() => {
    const problems = issues.filter((issue) => issue.type !== 'valid').length;
    const aiProblems = aiConflictWarning ? 1 : 0;
    const total = problems + aiProblems;
    if (!total) return null;
    return total;
  }, [issues, aiConflictWarning]);

  const sentenceStatuses = useMemo(() => {
    const segments = prompt
      .split(/[\n.]+/)
      .map((text) => text.trim())
      .filter(Boolean)
      .slice(0, 8);

    return segments.map((segment, idx) => {
      const start = prompt.toLowerCase().indexOf(segment.toLowerCase());
      const end = start + segment.length;
      const localIssues = issues.filter((issue) => issue.start < end && issue.end > start && issue.type !== 'valid');
      const state: SentenceState = localIssues.some((item) => item.type === 'conflict' || item.type === 'illogical')
        ? 'conflict'
        : localIssues.length
          ? 'weak'
          : 'good';
      return { id: `${idx}-${start}`, text: segment, state };
    });
  }, [issues, prompt]);

  const runAutoPipeline = async (basePrompt: string) => {
    if (!basePrompt || autoProcessingLockRef.current) return;
    autoProcessingLockRef.current = true;
    setLastPipelineStatus('fixing');
    setIsResolvingAll(true);
    try {
      const aiResult = await callPromptAI('fix', basePrompt);
      const latestPrompt = normalizeFixedPromptText(aiResult?.fixed_prompt?.trim() || basePrompt);
      const hardened = hardenFixedPrompt(latestPrompt, basePrompt);

      await commitFixedPromptSimple(hardened);
      setLastPipelineStatus('done');
      lastProcessedPromptRef.current = hardened || basePrompt;
    } catch (error) {
      console.error('Auto pipeline failed:', error);
      lastProcessedPromptRef.current = null;
    } finally {
      setIsResolvingAll(false);
      setIsEnhancing(false);
      autoProcessingLockRef.current = false;
    }
  };

  const applyFix = (issue: PromptIssue) => {
    if (!issue.fix?.replacement && issue.fix?.replacement !== '') return;
    const before = prompt.slice(0, issue.start);
    const after = prompt.slice(issue.end);
    const replacement = issue.fix.replacement;
    const nextPrompt = `${before}${replacement}${after}`.replace(/\s{2,}/g, ' ').replace(/,\s*,/g, ', ').trim();
    analyzeRequestIdRef.current += 1;
    intelRequestIdRef.current += 1;
    lastAnalyzedRef.current = '';
    hasUserEditedRef.current = true;
    latestPromptRef.current = nextPrompt;
    setPrompt(nextPrompt);
  };


  // Apply a fixed prompt without extra verification calls — the normal 4s debounced analysis handles re-checking
  // commit a new prompt value to state and immediately re‑run the inline
  // analyzer.  Prior implementation wiped `issues` instantly which gave the
  // impression that the "fix" button merely hid conflict markers instead of
  // resolving them.  The UI would stay clean for several seconds until the
  // debounced analyser fired again, at which point unresolved problems would
  // re‑appear.  That behaviour matched the description the user reported
  // ("زر fix بيصلح البرومبت ... هو بيلغي ui ux الي بيبين التضارب").
  //
  // The new implementation leaves whatever issues were already shown in place
  // while a fresh analysis is performed, then replaces them with the latest
  // result.  If the fix actually removed the conflicts they disappear almost
  // immediately; if not the markers remain so the user sees something still
  // needs attention.
  const commitFixedPromptSimple = async (nextPrompt: string) => {
    // ensure basic cleaning is applied before we start deduping.  this will
    // remove repeated segments, empty parentheses, etc.
    nextPrompt = cleanFinalPrompt(nextPrompt);

    // if the new prompt somehow still contains the old prompt verbatim more
    // than once (common when the AI appends rather than rewrites), strip out
    // the duplicate portion before we update state.  We look at the current
    // value tracked in latestPromptRef because the parent might already have
    // asynchronously updated the prop before we commit.
    const original = latestPromptRef.current || '';
    nextPrompt = dedupeAgainstSource(nextPrompt, original);
    // a second cleanup pass after deduping to catch any leftover quirks
    nextPrompt = cleanFinalPrompt(nextPrompt);

    analyzeRequestIdRef.current += 1;
    intelRequestIdRef.current += 1;
    canSyncPromptRef.current = true;
    hasUserEditedRef.current = true;
    latestPromptRef.current = nextPrompt;

    // update the prompt text first; don’t clear the existing issues just yet –
    // that would remove the visual context while the new analysis is running.
    setPrompt(nextPrompt);
    setIgnoredIssueIds(new Set());
    setSelectedIssueId(null);
    setPromptHealth(100);
    setAdvancedReport(null);

    // run a fresh analysis synchronously and update the UI as soon as we have
    // results rather than waiting for the debounced effect later on.
    setIsAnalyzing(true);
    lastAnalyzedRef.current = '';
    try {
      const result = await analyzePromptWithGrok(nextPrompt.trim(), safeCanvasSubject);
      setPromptHealth(result.health);
      setAdvancedReport(result.advancedReport ?? null);
      // we already cleared ignoredIssueIds above so there's nothing to filter out
      setIssues(result.issues);
      lastAnalyzedRef.current = nextPrompt.trim();
    } catch (err) {
      console.error('prompt re‑analysis failed', err);
      // keep whatever issues we had before rather than clearing them entirely.
    } finally {
      setIsAnalyzing(false);
    }
  };

  // expose the helper for tests if requested
  if (onTestCommitFixed) {
    onTestCommitFixed(commitFixedPromptSimple);
  }

  const convergeFixedPrompt = async (sourcePrompt: string, seedPrompt: string): Promise<string> => {
    let current = hardenFixedPrompt(seedPrompt, sourcePrompt);

    for (let pass = 0; pass < 3; pass += 1) {
      try {
        const analyzed = await analyzePromptWithGrok(current, safeCanvasSubject);
        const unresolved = activeIssues(analyzed.issues);
        if (!unresolved.length) return current;

        // although we already ran grok above in the outer loop, we also
      // normalise every intermediate candidate to avoid drift during
      // convergence.  The callPromptAI may still perform additional polishing.
      const aiResult = await callPromptAI('fix', current, {
          canvasSubject: safeCanvasSubject || undefined,
          sceneContext: initialPrompt || undefined,
        });

        let aiFixed = normalizeFixedPromptText(aiResult?.fixed_prompt?.trim() || '');
        aiFixed = dedupeAgainstSource(aiFixed, sourcePrompt);
        // if the AI has re‑ordered the words in a way that doesn't preserve
        // the original sequence, ignore its suggestion and stick with the
        // previously computed `current` (which has already been cleaned by
        // grok).  This stops the header‑rewrite problem the user described.
        if (!preservesWordOrder(aiFixed, sourcePrompt)) {
          aiFixed = current;
        }
        const deterministic = hardenFixedPrompt(resolveAllWithGrok(current, unresolved), current);
        const next = hardenFixedPrompt(aiFixed || deterministic, deterministic || current);

        if (!next || next === current) {
          return deterministic || current;
        }

        current = next;
      } catch {
        return hardenFixedPrompt(resolveAllPromptIssues(current), sourcePrompt);
      }
    }

    return current;
  };

  const acceptRebuiltPrompt = () => {
    // After a state-based fix, allow the rebuilt prompt to flow through
    // but mark it as already analyzed so the Grok analyzer doesn't re-flag it
    hasUserEditedRef.current = false;
    canSyncPromptRef.current = true;
    analyzeRequestIdRef.current += 1;
    intelRequestIdRef.current += 1;
    setIssues([]);
    setIgnoredIssueIds(new Set());
    setSelectedIssueId(null);
    setPromptHealth(100);
    setLastPipelineStatus('done');
    // Mark as analyzed AFTER a short delay to catch the rebuilt prompt
    setTimeout(() => {
      lastAnalyzedRef.current = latestPromptRef.current.trim();
    }, 200);
  };

  const handleAIFixSingle = async (issue: PromptIssue) => {
    // 1. Try state-based fix first (fixes source of truth)
    if (onFixSingleIssue) {
      onFixSingleIssue(issue);
      acceptRebuiltPrompt();
      return;
    }

    // 2. Fallback: text-based fix (legacy path)
    const sourcePrompt = prompt;
    const issueText = sourcePrompt.slice(issue.start, issue.end).trim();
    const deterministicFixed = applyDeterministicSingleFix(sourcePrompt, issue);

    try {
      const resp = await fetch(EDGE_FN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          prompt: sourcePrompt,
          mode: 'fix-single',
          issueText,
          issueType: issue.type,
          issueMessage: issue.message,
          canvasSubject: safeCanvasSubject || undefined,
          sceneContext: initialPrompt || undefined,
        }),
      });

      if (!resp.ok) {
        const convergedFallback = await convergeFixedPrompt(sourcePrompt, deterministicFixed);
        await commitFixedPromptSimple(convergedFallback);
        return;
      }

      const data = await resp.json();
      const aiFixed = normalizeFixedPromptText(data?.fixed_prompt?.trim() || '');

      const candidate = aiFixed && !issueStillPresentAfterFix(sourcePrompt, aiFixed, issueText, issue.type)
        ? aiFixed
        : deterministicFixed;

      const converged = await convergeFixedPrompt(sourcePrompt, candidate);
      await commitFixedPromptSimple(converged);
      setLastPipelineStatus('done');
    } catch {
      const convergedFallback = await convergeFixedPrompt(sourcePrompt, deterministicFixed);
      await commitFixedPromptSimple(convergedFallback);
    }
  };

  const ignoreIssue = (issueId: string) => {
    setIgnoredIssueIds((prev) => new Set(prev).add(issueId));
  };

  const handleFixPrompt = async () => {
    if (isResolvingAll) return;
    setLastPipelineStatus('fixing');
    setIsResolvingAll(true);

    try {
      const smartDefaults = getSmartDefaults(prompt);
      const fixed = await fixPrompt(prompt, {
        cameraAngle: promptIntel?.understoodScene.camera || smartDefaults.cameraAngle || '',
        lightingStyle: promptIntel?.understoodScene.lighting || smartDefaults.lighting || '',
        shotSize: promptIntel?.understoodScene.shotScale || smartDefaults.shotSize || '',
        lens: smartDefaults.lens || '',
        composition: smartDefaults.composition || '',
        depthOfField: smartDefaults.depthOfField || '',
        smartDefaults: [smartDefaults.atmosphere, smartDefaults.rimLight ? 'rim light enabled' : ''].filter(Boolean).join(', '),
      });
      await commitPromptChange(fixed || prompt);
      setLastPipelineStatus('done');
    } catch (error) {
      console.error('[Fix] Error:', error);
      setAiConflictWarning('AI temporarily unavailable. Please try again.');
    } finally {
      setIsResolvingAll(false);
    }
  };

  // commit helper used by both fix and enhance paths; ensures issues are
  // retained until a fresh analysis completes and flags sync behaviour.
  const commitPromptChange = async (nextPrompt: string) => {
    // apply a clean pass so that even simple edits don't accumulate duplicates
    nextPrompt = cleanFinalPrompt(nextPrompt);

    // reuse the same logic as commitFixedPromptSimple but without the fix
    // convergence loop.  This avoids duplicating the issue-retention logic.
    analyzeRequestIdRef.current += 1;
    intelRequestIdRef.current += 1;
    canSyncPromptRef.current = true;
    hasUserEditedRef.current = true;
    latestPromptRef.current = nextPrompt;

    setPrompt(nextPrompt);
    setIgnoredIssueIds(new Set());
    setSelectedIssueId(null);
    setPromptHealth(100);
    setAdvancedReport(null);

    setIsAnalyzing(true);
    lastAnalyzedRef.current = '';
    try {
      const result = await analyzePromptWithGrok(nextPrompt.trim(), safeCanvasSubject);
      setPromptHealth(result.health);
      setAdvancedReport(result.advancedReport ?? null);
      setIssues(result.issues);
      lastAnalyzedRef.current = nextPrompt.trim();
    } catch (err) {
      console.error('prompt re‑analysis failed', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleEnhancePrompt = async () => {
    if (!prompt.trim() || isEnhancing) return;
    setLastPipelineStatus('enhancing');
    setIsEnhancing(true);

    try {
      const smartDefaults = getSmartDefaults(prompt);
      const enhanced = await enhancePrompt(prompt, {
        cameraAngle: promptIntel?.understoodScene.camera || smartDefaults.cameraAngle || '',
        lightingStyle: promptIntel?.understoodScene.lighting || smartDefaults.lighting || '',
        shotSize: promptIntel?.understoodScene.shotScale || smartDefaults.shotSize || '',
        lens: smartDefaults.lens || '',
        composition: smartDefaults.composition || '',
        depthOfField: smartDefaults.depthOfField || '',
        smartDefaults: [smartDefaults.atmosphere, smartDefaults.rimLight ? 'rim light enabled' : ''].filter(Boolean).join(', '),
      });
      await commitPromptChange(enhanced || prompt);
      setLastPipelineStatus('done');
    } catch (error) {
      console.error('[Enhance] Error:', error);
      setAiConflictWarning('AI temporarily unavailable. Please try again.');
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleEnhanceCinematicGrammar = async () => {
    if (!prompt.trim() || isEnhancingGrammar) return;
    setIsEnhancingGrammar(true);
    try {
      const next = await enhanceCinematicGrammarWithGrok(prompt);
      if (next && next !== prompt) {
        await commitPromptChange(next || prompt);
      }
    } finally {
      setIsEnhancingGrammar(false);
    }
  };

  const handleFixPhysics = async () => {
    if (!prompt.trim() || isFixingPhysics) return;
    setIsFixingPhysics(true);
    try {
      const next = await resolvePhysicsWithGrok(prompt);
      if (next && next !== prompt) {
        await commitPromptChange(next || prompt);
      }
    } finally {
      setIsFixingPhysics(false);
    }
  };


  const handleCopyClean = async () => {
    if (zeroConflictMode && (promptIntel?.conflictScore.count || 0) > 0) return;
    if (!prompt.trim()) return;
    try {
      await navigator.clipboard.writeText(prompt.trim());
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch (error) {
      console.error('Failed to copy prompt:', error);
    }
  };

  const cyclePanelSize = () => {
    setPanelSize(prev => prev === 'collapsed' ? 'compact' : prev === 'compact' ? 'expanded' : 'collapsed');
  };

  const panelHeight = panelSize === 'collapsed' ? 'h-[52px]' : panelSize === 'compact' ? 'h-[180px]' : 'h-[320px]';

  const statusDot = lastPipelineStatus === 'fixing' || lastPipelineStatus === 'enhancing' || lastPipelineStatus === 'analyzing'
    ? 'bg-amber-400 animate-pulse'
    : issueCountLabel ? 'bg-orange-400' : 'bg-emerald-400';

  return (
    <div className="flex flex-col">
      <motion.div
        className={`prompt-console ${panelHeight} border-t border-border/30 flex flex-col transition-all duration-300 ease-out overflow-hidden`}
        layout
      >
      {/* ── Top Bar (always visible) ── */}
      <div className="flex items-center justify-between gap-2 px-4 py-2.5 min-h-[48px] shrink-0">
        {/* Left: status + health */}
        <div className="flex items-center gap-3">
          <button onClick={cyclePanelSize} className="flex items-center gap-2 group">
            <div className={`w-2 h-2 rounded-full ${statusDot} transition-colors`} />
            <span className="text-[11px] font-semibold text-foreground/90 tracking-tight group-hover:text-primary transition-colors">{t('prompt.title')}</span>
            {panelSize === 'collapsed' ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground/50" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground/50" />}
          </button>

          {/* Health bar */}
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 rounded-full bg-muted/40 overflow-hidden">
              <motion.div
                className={`h-full rounded-full ${getHealthBg(promptHealth)}`}
                initial={{ width: 0 }}
                animate={{ width: `${promptHealth}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <span className={`text-[10px] font-mono tabular-nums ${getHealthTone(promptHealth).split(' ')[0]}`}>{promptHealth}%</span>
          </div>

          {issueCountLabel && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-orange-500/10 text-orange-300 border border-orange-500/20">
              {issueCountLabel} {(issueCountLabel ?? 0) > 1 ? t('prompt.issues') : t('prompt.issue')}
            </span>
          )}


          {isAnalyzing && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground/50" />}

          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded-md border ${
            lastPipelineStatus === 'fixing' ? 'border-red-500/30 text-red-300 bg-red-500/5' :
            lastPipelineStatus === 'enhancing' ? 'border-blue-500/30 text-blue-300 bg-blue-500/5' :
            lastPipelineStatus === 'analyzing' ? 'border-orange-500/30 text-orange-300 bg-orange-500/5' :
            'border-emerald-500/30 text-emerald-300 bg-emerald-500/5'
          }`}>
            {lastPipelineStatus === 'fixing' ? t('prompt.fixing') : lastPipelineStatus === 'enhancing' ? t('prompt.enhancing') : lastPipelineStatus === 'analyzing' ? t('prompt.analyzing') : t('prompt.ready')}
          </span>
        </div>

        {/* Right: actions */}
        <div className="flex items-center gap-1.5">
          <ActionButton
            onClick={handleFixPrompt}
            disabled={locked || isResolvingAll || !prompt.trim() || isPlaceholderPrompt(prompt)}
            loading={isResolvingAll}
            label={t('prompt.fix')}
            loadingLabel={t('prompt.fixing')}
            variant="default"
          />
          <ActionButton
            onClick={handleEnhancePrompt}
            disabled={locked || isEnhancing || !prompt.trim()}
            loading={isEnhancing}
            label={t('prompt.enhance')}
            loadingLabel={t('prompt.enhancing')}
            variant="primary"
          />

          {expertMode && (
            <>
              <ActionButton onClick={handleFixPhysics} disabled={locked || isFixingPhysics || !issues.some((issue) => issue.message.includes('[Physics'))} loading={isFixingPhysics} label={t('prompt.physics')} loadingLabel={t('prompt.fixing')} variant="warning" />
              <ActionButton onClick={handleEnhanceCinematicGrammar} disabled={locked || isEnhancingGrammar || !prompt.trim()} loading={isEnhancingGrammar} label={t('prompt.grammar')} loadingLabel={t('prompt.enhancing')} variant="info" />
            </>
          )}

          <div className="w-px h-5 bg-border/30 mx-0.5" />

          <motion.button whileTap={{ scale: 0.93 }} type="button" onClick={onSave} disabled={locked || (zeroConflictMode && (promptIntel?.conflictScore.count || 0) > 0)} className="prompt-action-btn">
            <Save className="w-3.5 h-3.5" />
          </motion.button>
          <motion.button whileTap={{ scale: 0.93 }} type="button" onClick={handleCopyClean} disabled={locked || (zeroConflictMode && (promptIntel?.conflictScore.count || 0) > 0)} className="prompt-action-btn">
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          </motion.button>

          <div className="w-px h-5 bg-border/30 mx-0.5" />

          <button onClick={() => setExpertMode(p => !p)} className={`text-[9px] font-mono px-2 py-1 rounded-md border transition-all ${expertMode ? 'border-primary/40 text-primary bg-primary/10' : 'border-border/30 text-muted-foreground/60 hover:text-primary'}`}>
            {expertMode ? t('prompt.expert').toUpperCase() : t('prompt.expert')}
          </button>
          <button onClick={() => setZeroConflictMode(p => !p)} className={`text-[9px] font-mono px-2 py-1 rounded-md border transition-all ${zeroConflictMode ? 'border-red-500/30 text-red-300 bg-red-500/5' : 'border-border/30 text-muted-foreground/60'}`}>
            {t('prompt.zeroConflict')}
          </button>
          <button onClick={cyclePanelSize} className="prompt-action-btn" title="Resize panel">
            {panelSize === 'expanded' ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* ── Collapsible Content ── */}
      <AnimatePresence>
        {panelSize !== 'collapsed' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col min-h-0 overflow-hidden px-4 pb-3"
          >
            {/* Format selector */}
            <div className="flex items-center gap-1 mb-2 pb-2 border-b border-border/20 flex-wrap">
              <span className="text-[9px] font-mono text-muted-foreground/50 mr-1.5 shrink-0">OUTPUT</span>
              {(['structured', 'natural', 'weighted', 'tags', 'json', 'yaml', 'dsl', 'graph', 'markdown', 'bullet'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => onFormatChange?.(f)}
                  className={`px-2 py-1 text-[9px] font-mono rounded-md border transition-all capitalize ${
                    promptFormat === f
                      ? 'border-primary/40 text-primary bg-primary/10'
                      : 'border-border/20 text-muted-foreground/50 hover:text-foreground hover:border-border/40 hover:bg-muted/20'
                  }`}
                >
                  {f}
                </button>
              ))}
              <div className="w-px h-4 bg-border/20 mx-0.5" />
              <button
                onClick={() => setShowNegativePrompt(p => !p)}
                className={`flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-mono rounded border transition-all ${
                  showNegativePrompt
                    ? 'border-destructive/40 text-destructive bg-destructive/10'
                    : 'border-border/20 text-muted-foreground/50 hover:text-foreground hover:border-border/40'
                }`}
              >
                <ShieldX className="w-2.5 h-2.5" />
                NEG
              </button>
              {showNegativePrompt && negativePrompt && (
                <>
                  <p className="text-[8px] font-mono text-destructive/50 truncate max-w-[200px]">
                    {formatNegativePrompt(negativePrompt, promptFormat as ExtendedPromptFormat).split('\n')[0].slice(0, 80)}…
                  </p>
                  <button
                    onClick={async () => {
                      try {
                        const formatted = formatNegativePrompt(negativePrompt, promptFormat as ExtendedPromptFormat);
                        await navigator.clipboard.writeText(formatted);
                        setCopiedNeg(true);
                        window.setTimeout(() => setCopiedNeg(false), 1200);
                      } catch {}
                    }}
                    className="prompt-action-btn !p-0.5 shrink-0"
                    title="Copy Negative Prompt"
                  >
                    {copiedNeg ? <Check className="w-2.5 h-2.5 text-emerald-400" /> : <Copy className="w-2.5 h-2.5 text-muted-foreground/40" />}
                  </button>
                </>
              )}
            </div>

            {zeroConflictMode && (promptIntel?.conflictScore.count || 0) > 0 && (
              <p className="text-[9px] font-mono text-red-300 mb-1">{t('prompt.zeroConflictMsg')}</p>
            )}

            {/* Sentence status chips */}
            {panelSize === 'expanded' && (
              <div className="flex flex-wrap gap-1 mb-2">
                {sentenceStatuses.map((segment) => (
                  <span
                    key={segment.id}
                    className={`text-[9px] font-mono px-2 py-0.5 rounded-md border ${
                      segment.state === 'conflict' ? 'border-red-500/30 text-red-300 bg-red-500/5' :
                      segment.state === 'weak' ? 'border-orange-500/30 text-orange-200 bg-orange-500/5' :
                      'border-emerald-500/30 text-emerald-300 bg-emerald-500/5'
                    }`}
                  >
                    {segment.text.length > 60 ? segment.text.slice(0, 60) + '…' : segment.text}
                  </span>
                ))}
              </div>
            )}





            {/* Main content grid */}
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_240px] gap-2 min-h-0 flex-1">
              <InlinePromptEditor
                value={prompt}
                onChange={(value) => {
                  hasUserEditedRef.current = true;
                  canSyncPromptRef.current = true;
                  setPrompt(value);
                }}
                issues={issues}
                onApplyFix={applyFix}
                onAIFixSingle={handleAIFixSingle}
                onIgnoreIssue={ignoreIssue}
                onSelectIssue={setSelectedIssueId}
                selectedIssueId={selectedIssueId}
                disabled={locked}
              />

              {/* Issues & Intelligence sidebar */}
              <div className="rounded-lg border border-border/20 bg-muted/5 p-2 overflow-auto text-[10px] font-mono">
                {/* Issues list */}
                <div className="space-y-1 mb-2">
                  {issues.filter((issue) => issue.type !== 'valid').length === 0 && <p className="text-emerald-300/80">{t('prompt.noIssues')}</p>}
                  {issues.filter((issue) => issue.type !== 'valid').map((issue) => (
                    <button key={issue.id} type="button" onClick={() => setSelectedIssueId(issue.id)} className={`w-full text-left px-2 py-1 rounded-md border transition ${selectedIssueId === issue.id ? 'border-primary/40 bg-primary/5' : 'border-border/15 hover:border-primary/20'}`}>
                      <p className="text-foreground/80 capitalize text-[9px]">{issue.type}</p>
                      <p className="text-muted-foreground/70 line-clamp-1 text-[9px]">{issue.message}</p>
                    </button>
                  ))}
                </div>

                {/* Intelligence toggle */}
                {promptIntel && (
                  <button
                    onClick={() => setShowInspector(p => !p)}
                    className="w-full text-left text-[9px] font-mono text-primary/70 hover:text-primary py-1 border-t border-border/15 mt-1"
                  >
                    {showInspector ? t('prompt.hideIntel') : t('prompt.showIntel')}
                  </button>
                )}

                {promptIntel && showInspector && (
                  <div className="space-y-1.5 mt-1 text-[9px]">
                    <IntelRow label={t('intel.subject')} value={promptIntel.understoodScene.subject} />
                    <IntelRow label={t('intel.action')} value={promptIntel.understoodScene.action} />
                    <IntelRow label={t('intel.environment')} value={promptIntel.understoodScene.environment} />
                    <IntelRow label={t('intel.mood')} value={promptIntel.understoodScene.mood} />
                    <IntelRow label={t('intel.camera')} value={promptIntel.understoodScene.camera} />

                    <div className="border-t border-border/15 pt-1 mt-1">
                      <IntelRow label={t('intel.conflicts')} value={`${promptIntel.conflictScore.level} • ${promptIntel.conflictScore.count}`} />
                      <IntelRow label={t('intel.score')} value={`${promptIntel.promptScore}/100`} />
                      <IntelRow label={t('intel.cinematic')} value={`${promptIntel.cinematicQualityLevel} (${promptIntel.cinematicGrammar.score}/100)`} />
                    </div>

                    {panelSize === 'expanded' && (
                      <>
                        <div className="border-t border-border/15 pt-1 mt-1">
                          <p className="text-primary/70 mb-0.5">{t('prompt.autoDirector')}</p>
                          <IntelRow label={t('intel.camera')} value={promptIntel.autoDirector.camera} />
                          <IntelRow label={t('intel.lens')} value={promptIntel.autoDirector.lens} />
                          <IntelRow label={t('intel.lighting')} value={promptIntel.autoDirector.lighting} />
                        </div>

                        <div className="border-t border-border/15 pt-1 mt-1">
                          <p className="text-primary/70 mb-0.5">{t('prompt.settingsHistory')}</p>
                          {settingsHistory.length === 0 && <p className="text-muted-foreground/50">{t('prompt.noPresets')}</p>}
                          {settingsHistory.map((preset, idx) => (
                            <button
                              key={`settings-${idx}`}
                              type="button"
                              onClick={() => onApplySettingsPreset?.(preset)}
                              className="w-full text-left rounded-md border border-border/15 px-2 py-1 hover:border-primary/30 hover:bg-primary/5 transition-colors mb-0.5"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-primary/60 text-[8px]">Preset {settingsHistory.length - idx}</span>
                                <RotateCcw className="w-2.5 h-2.5 text-primary/50" />
                              </div>
                              <p className="text-muted-foreground/60 line-clamp-1 text-[8px]">{preset.camera} • {preset.lens}</p>
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {expertMode && advancedReport && (
                  <div className="mt-2 rounded-md border border-primary/15 bg-primary/5 p-2 space-y-1">
                    <p className="text-primary/80 text-[9px]">{t('prompt.inspector')}</p>
                    <p className="text-[9px]">Score: {advancedReport.promptScore}/100</p>
                    <p className="text-muted-foreground/60 text-[8px] line-clamp-2">{advancedReport.finalCleanPrompt.slice(0, 120)}</p>
                  </div>
                )}
              </div>
            </div>


          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
    </div>
  );
}

function ActionButton({ onClick, disabled, loading, label, loadingLabel, variant }: {
  onClick: () => void; disabled: boolean; loading: boolean; label: string; loadingLabel: string;
  variant: 'default' | 'primary' | 'warning' | 'info';
}) {
  const colors = {
    default: 'border-primary/20 text-primary/80 hover:bg-primary/5',
    primary: 'border-primary/30 text-primary hover:bg-primary/10',
    warning: 'border-orange-400/25 text-orange-300 hover:bg-orange-500/5',
    info: 'border-blue-400/25 text-blue-300 hover:bg-blue-500/5',
  };
  return (
    <motion.button
      whileTap={{ scale: 0.93 }}
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-mono border ${colors[variant]} disabled:opacity-30 transition-colors`}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      {loading ? loadingLabel : label}
    </motion.button>
  );
}

function IntelRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground/50 shrink-0">{label}</span>
      <span className="text-foreground/70 text-right truncate">{value}</span>
    </div>
  );
}
